import {
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useMutation} from '@github-ui/react-query'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useSuggestionBatch, useSuggestionBatchActions} from '../stores/selectors'
import {clearActiveBatchFromStorage} from '../helpers/suggestion-batch-storage'

type UnifiedComment = {
  id: number
  suggestionIndex?: number
}

/**
 * Applies a batch of suggested changes by extracting suggestions from comments stored in the active batch.
 */
export function useApplyBatchedSuggestionsMutation(pullRequestPath: string, headOid: string) {
  const apiUrl = usePageDataUrl(PageData.applySuggestedChanges)
  const suggestionBatch = useSuggestionBatch()
  const {clearSuggestionBatch} = useSuggestionBatchActions()

  return useMutation({
    mutationFn: async ({message}: {message: string}) => {
      const comments: UnifiedComment[] = suggestionBatch.activeBatch.map(item => {
        const comment: UnifiedComment = {id: item.commentDatabaseId}
        if (item.suggestionIndex != null) {
          comment.suggestionIndex = item.suggestionIndex
        }
        return comment
      })

      if (comments.length === 0) {
        throw new Error('No valid suggestions found in batch')
      }

      const response = await reactFetchJSON(apiUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {
          comments,
          message,
          expectedHeadOid: headOid,
        },
      })

      const json = await parseJSONWithBetterErrors(response)

      if (!response.ok && json?.code === 'HEAD_OID_MISMATCH') {
        throw new Error(
          'The pull request has been updated since this batch was created. Please refresh the page and try again.',
          {cause: response.status},
        )
      }

      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: () => {
      clearSuggestionBatch()
      clearActiveBatchFromStorage(pullRequestPath, headOid)

      ssrSafeWindow?.location.reload()
    },
  })
}
